export type Role = 'user' | 'employee' | 'developer' | 'admin' | 'root'
export type Visibility = 'public' | 'internal'

/** Roles that may read `internal` docs. Mirrors the backend's staff check. */
export const STAFF_ROLES: readonly Role[] = ['employee', 'developer', 'admin', 'root']
export const ADMIN_ROLES: readonly Role[] = ['admin', 'root']

/** Authoring: create and update services, topics and docs. A plain `user` cannot. */
export const WRITE_ROLES: readonly Role[] = ['employee', 'developer', 'admin', 'root']

/**
 * Who is shown the token screen. The backend is the authority on who may actually
 * mint one — an `employee` asking for the `write` scope comes back `cannot_write` —
 * so this only decides visibility, and refusals are surfaced verbatim.
 */
export const TOKEN_ROLES: readonly Role[] = ['employee', 'developer', 'admin', 'root']

export const isStaff = (r: Role | null) => !!r && STAFF_ROLES.includes(r)
export const isAdmin = (r: Role | null) => !!r && ADMIN_ROLES.includes(r)
export const canWrite = (r: Role | null) => !!r && WRITE_ROLES.includes(r)
export const canIssueTokens = (r: Role | null) => !!r && TOKEN_ROLES.includes(r)
/** Destruction — a doc, a branch, a whole service — is admin-only. */
export const canDelete = isAdmin

export interface Me {
  email: string
  /** Absent on older backends; fall back to the email before showing nothing. */
  name?: string
  role: Role
}

export interface DocSummary {
  id: number
  service: string
  /** Full slash path, e.g. `doctor-desk/exams` — not a single segment. */
  topic: string
  slug: string
  title: string
  description: string
  visibility: Visibility
  tags: string[]
  updated_at: string
  author?: Actor
  last_edited_by?: Actor
}

/**
 * Who wrote or last touched a doc. A machine — CI, an IDE agent — is shown as a
 * service badge rather than a person, because nobody has that inbox.
 */
export interface Actor {
  id?: number
  /** What to render. The backend fills it with the email when no name is known. */
  name?: string
  email: string
  machine?: boolean
}

/**
 * Prefer `name`, as the backend asks. It falls back to the email only because
 * production has not shipped the field yet — an empty byline would be worse.
 */
/**
 * The canonical URL of a doc: `/glossary/en/rx/od`, not `/d/126`. The keyword
 * belongs in the path — `/d/126` says nothing to a search engine or a reader.
 */
export const docPath = (d: Pick<DocSummary, 'service' | 'topic' | 'slug'>) =>
  `/${[d.service, ...d.topic.split('/').filter(Boolean), d.slug].join('/')}`

export const actorLabel = (a?: Actor) => a?.name || a?.email
export const actorEmail = (a?: Actor) => a?.email
export const actorIsMachine = (a?: Actor) => !!a?.machine

/** Where the rendered page body lives. The backend never serves bodies itself. */
export interface PagePointer {
  /** `hosting` → fetch `location` directly. `storage` → resolve via the Storage SDK. */
  source: 'hosting' | 'storage'
  location: string
}

export interface DocDetail extends DocSummary {
  page: PagePointer
  votes: number
  favorited: boolean
  /**
   * The backend decides who may edit and delete, and says so per document. The UI
   * renders these verbatim: recomputing the rule here would invent a second, drifting
   * source of truth — and the client is not the guard in any case.
   */
  can_edit?: boolean
  can_delete?: boolean
}

export interface TreeDoc {
  id: number
  slug: string
  title: string
  description: string
  visibility: Visibility
  tags: string[]
}
/**
 * Topics nest arbitrarily deep: `core-backend-api → doctor-desk → exams → docs`.
 * A leaf simply has no `topics`, so the old two-level response is a valid case of
 * this shape and needs no special handling.
 */
/**
 * The backend sends `null`, not `[]`, for an empty list — and a default parameter
 * value only fills in `undefined`. Every list in the tree is therefore nullable,
 * and every read of one has to go through `list()`.
 */
export type Maybe<T> = T[] | null | undefined
const list = <T,>(v: Maybe<T>): T[] => v ?? []

export interface TreeTopic {
  slug: string
  /** Full slash path from the service root, e.g. `doctor-desk/exams`. */
  path: string
  title: string
  docs?: Maybe<TreeDoc>
  topics?: Maybe<TreeTopic>
}
export interface TreeService {
  slug: string
  title: string
  topics?: Maybe<TreeTopic>
  /** Docs pinned directly under the service, outside any topic. */
  docs?: Maybe<TreeDoc>
}

/** Every doc in the tree, at any depth. */
export function flattenTree(services: Maybe<TreeService>): TreeDoc[] {
  const out: TreeDoc[] = []

  const walk = (topics: Maybe<TreeTopic>) => {
    for (const t of list(topics)) {
      out.push(...list(t.docs))
      walk(t.topics)
    }
  }

  for (const s of list(services)) {
    out.push(...list(s.docs))
    walk(s.topics)
  }
  return out
}

export { list as listOf }

export interface Facet {
  tag: string
  count: number
}
export interface SearchResponse {
  count: number
  results: Maybe<DocSummary>
  facets: Maybe<Facet>
}

export interface Comment {
  id: number
  doc_id: number
  user_id: number
  parent_id: number | null
  body: string
  created_at: string
}

/** A comment with its replies resolved from `parent_id`. */
export interface CommentNode extends Comment {
  replies: CommentNode[]
}

export interface User {
  id: number
  email: string
  role: Role
  active: boolean
  /** A machine identity: it never signs in with Google, it only carries tokens. */
  machine?: boolean
}

/** A machine must never hold admin power, so its role is capped below it. */
export const MACHINE_ROLES: readonly Role[] = ['user', 'employee', 'developer']

export type Scope = 'write'

export interface Token {
  id: number
  name?: string
  /** Non-secret identifier, e.g. `whk_8328937`. Safe to display and to log. */
  prefix: string
  scopes?: Scope[]
  revoked?: boolean
  expires_at?: string
  last_used_at?: string
  created_at?: string
  /** Email of the identity the token acts as. May be a machine. */
  owner?: string
  owner_is_machine?: boolean
  /** Who requested it — an admin, when issued for a machine. Omitted when equal to owner. */
  created_by?: string
  /**
   * The secret, returned exactly once by the endpoint that creates the token.
   * Absent from every listing; if it is lost here it cannot be recovered.
   */
  raw_token?: string
}
